<template>
  <div
    class="play-table"
    :class="{ 'play-table--responsive': responsive }"
  >
    <table
      class="play-table__table"
      :class="[
        {
          'play-table__table--hover': hover,
          'play-table__table--striped': striped,
          'play-table__table--bordered': bordered,
          'play-table__table--sm': small,
        },
      ]"
    >
      <slot />
    </table>
  </div>
</template>

<script>
/**
 * PlayTable — simple styled table for the Play system.
 *
 * Token-driven (see src/styles/tokens.scss). Replaces bootstrap-vue's
 * <b-table-simple> + <b-thead>/<b-tbody>/<b-tr>/<b-th>/<b-td>: drop NATIVE
 * `<thead>/<tbody>/<tr>/<th>/<td>` inside the default slot and they pick up the
 * Play look via scoped descendant styles (no per-cell wrapper components). The
 * `hover`, `striped`, `bordered`, `small`, and `responsive` props mirror the
 * b-table-simple flags.
 *
 * VUE3-MIGRATION: nothing Vue 2-specific — already Vue 3-safe.
 */
export default {
  name: 'PlayTable',
  props: {
    hover: {
      type: Boolean,
      default: false,
    },
    striped: {
      type: Boolean,
      default: false,
    },
    bordered: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    /** Wrap in a horizontally-scrollable container on narrow screens */
    responsive: {
      type: Boolean,
      default: false,
    },
  },
}
</script>

<style lang="scss" scoped>
.play-table {
  &--responsive {
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-family: var(--font-body);
    color: var(--text-body);
    background: var(--surface-card);
    border: var(--border-w) solid var(--border-ink);
    border-radius: var(--radius-lg);
    overflow: hidden;

    /* native cells, styled via descendant selectors */
    ::v-deep th,
    ::v-deep td {
      padding: 12px 16px;
      text-align: left;
      vertical-align: top;
      border-bottom: var(--border-w) solid var(--line);
    }

    ::v-deep th {
      font-family: var(--font-display);
      font-weight: 700;
      color: var(--text-strong);
      background: var(--surface-bg);
    }

    ::v-deep thead th {
      border-bottom: var(--border-w) solid var(--border-ink);
    }

    ::v-deep tr:last-child > th,
    ::v-deep tr:last-child > td {
      border-bottom: 0;
    }

    /* ── Flags ── */
    &--hover ::v-deep tbody tr {
      transition: background var(--dur-fast) var(--ease-out);

      &:hover { background: var(--c-secondary-soft); }
    }

    &--striped ::v-deep tbody tr:nth-of-type(odd) {
      background: var(--surface-bg);
    }

    &--bordered {
      ::v-deep th,
      ::v-deep td { border: var(--border-w) solid var(--line); }
    }

    &--sm ::v-deep {
      th,
      td { padding: 7px 10px; }
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .play-table__table--hover ::v-deep tbody tr { transition: none; }
}
</style>
